import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Textarea } from './textarea';
import { ElchmeBlueIcon, ElchmeRedIcon, ElchmeGreenIcon, ElchmeGoldIcon } from './legendary-icons';
import { 
  ChevronRight, ChevronDown, RefreshCw, Heart, Shield, Users, 
  Flame, TreePine, Wind, Sprout, BookOpen, Compass, Zap, 
  Circle, Target, ArrowRight
} from 'lucide-react';

const ELCHME_PHASES = [
  {
    key: 'blue',
    name: 'Observe',
    color: '#4A90D9',
    icon: ElchmeBlueIcon,
    prompt: 'What do you observe? See without judgment. What would a camera record?',
    description: 'Pure observation without interpretation'
  },
  {
    key: 'red',
    name: 'Feel',
    color: '#D94A4A',
    icon: ElchmeRedIcon,
    prompt: 'What feelings arise? Feelings are messengers from your needs.',
    description: 'Honor the emotional signals'
  },
  {
    key: 'green',
    name: 'Need',
    color: '#4AD94A',
    icon: ElchmeGreenIcon,
    prompt: 'What universal human needs are present? Connection, safety, autonomy, meaning?',
    description: 'Identify the root need'
  },
  {
    key: 'gold',
    name: 'Gold',
    color: '#D9B74A',
    icon: ElchmeGoldIcon,
    prompt: 'What resolution honors everyone\'s needs? What is the golden path forward?',
    description: 'Find the resolution that serves all'
  }
];

const QUICK_TOOLS = [
  {
    key: 'repair',
    name: 'Repair Flow',
    icon: RefreshCw,
    description: 'Navigate conflict toward repair'
  },
  {
    key: 'capacity',
    name: 'Capacity Check',
    icon: Shield,
    description: 'Assess current bandwidth'
  },
  {
    key: 'boundary',
    name: 'Boundary Work',
    icon: Heart,
    description: 'Clarify and communicate boundaries'
  }
];

// The Practical Blueprint - Fire Roles Framework
const FIRE_ROLES = [
  {
    key: 'fire_keeper',
    name: 'Fire Keeper',
    icon: Flame,
    color: '#D97706',
    description: 'Those who regulate themselves under stress and carry the long-term vision',
    traits: [
      'Regulate themselves under stress',
      'Follow through without being chased',
      'Hold boundaries without drama',
      'Navigate conflict without collapsing',
      'Take responsibility without resentment',
      'Stay steady when others panic',
      'Don\'t need the community to fix them'
    ],
    wisdom: 'A Fire Keeper is revealed in crisis, not in circle.'
  },
  {
    key: 'fire_sitter',
    name: 'Fire Sitter',
    icon: TreePine,
    color: '#059669',
    description: 'The majority who need a pathway to grow into greater responsibility',
    traits: [
      'Learn to take small responsibilities',
      'Need clear roles with clear expectations',
      'Build conflict capacity over time',
      'Model emotional regulation',
      'Contribute to the fire',
      'Receive feedback without shame',
      'Share in labor, not just space'
    ],
    wisdom: 'A Fire Sitter becomes a Fire Keeper through practice, not personality.'
  },
  {
    key: 'fire_runner',
    name: 'Fire Runner',
    icon: Wind,
    color: '#6366F1',
    description: 'Those who are unregulated, overwhelmed, and need structure to stabilize',
    traits: [
      'Need structure and predictability',
      'Require clear boundaries',
      'Benefit from mentorship',
      'Need clear expectations',
      'No enmeshment or rescuing',
      'Learn the culture first',
      'Held by a system, not a person'
    ],
    wisdom: 'A stabilized Fire Runner can become a powerful contributor—but only if the community doesn\'t collapse trying to save them.'
  },
  {
    key: 'stewardship',
    name: 'Stewardship Core',
    icon: Sprout,
    color: '#8B5CF6',
    description: 'The backbone of any lasting community',
    traits: [
      'Multiple Fire Keepers',
      'A few advanced Fire Sitters',
      'No one actively running',
      'Sets the culture',
      'Holds the boundaries',
      'Maintains the fire',
      'Makes the hard decisions'
    ],
    wisdom: 'Without a Stewardship Core, the founder becomes the default core—and that always ends in burnout.'
  }
];

const COMMUNITY_BUILDING = [
  {
    key: 'initiation',
    title: 'Design Initiation',
    content: 'Initiation is not gatekeeping—it\'s orientation + responsibility + culture transmission. Include: learning the agreements, expectations, boundaries, governance, labor structure, and how to tend the fire.',
    icon: BookOpen
  },
  {
    key: 'governance',
    title: 'Structure Governance',
    content: 'Governance is not hierarchy—it\'s clarity. Need: clear roles, authority, decision-making pathways, conflict processes, accountability, consequences, and communication channels.',
    icon: Shield
  },
  {
    key: 'culture',
    title: 'Protect the Fire',
    content: 'Culture is what people actually do: shared labor, emotional regulation, conflict literacy, boundaries as care, accountability as normal, clarity as kindness, no rescuing, no martyrdom.',
    icon: Flame
  }
];

// Foundational Truths - The invisible load-bearing beams
const FOUNDATIONAL_TRUTHS = [
  {
    key: 'systems_failure',
    title: 'Loneliness is a Systems Failure',
    content: 'Not a personal flaw. Isolation comes from hyper-mobility, individualism, digitization without embodiment, loss of ritual. Community must be DESIGNED, not hoped for.',
    icon: Users
  },
  {
    key: 'belonging_first',
    title: 'Belonging Precedes Contribution',
    content: 'People cannot collaborate until they feel seen, safe, and welcomed without performance. Connection is the precondition, not the reward.',
    icon: Heart
  },
  {
    key: 'learnable_skill',
    title: 'Community is Learnable',
    content: 'Hosting, inviting, prompting, sustaining—these are trainable behaviors, not innate gifts. You can build pipelines and curricula without betraying soul.',
    icon: BookOpen
  }
];

// Circle Process Elements
const CIRCLE_PROCESS = [
  {
    key: 'atomic_unit',
    title: 'Small Groups (6-12)',
    content: 'The atomic unit of community. This size allows safety, voice, and mutual recognition. Anything larger must be composed of small groups.',
    color: '#2f6f3e'
  },
  {
    key: 'equal_airtime',
    title: 'Equal Airtime, No Fixing',
    content: 'Listening without advice. No interrupting. No hierarchy of stories. This creates horizontal belonging.',
    color: '#b9935a'
  },
  {
    key: 'regulation_first',
    title: 'Regulation Before Revelation',
    content: 'Breath, grounding, arrival practices settle nervous systems, reduce performative sharing, and increase presence.',
    color: '#4a7c59'
  },
  {
    key: 'structure_safety',
    title: 'Structure Creates Safety',
    content: 'Clear start/end time, shared agreements, prompts or activities, closing ritual. Freedom increases when the container is clear.',
    color: '#6366f1'
  }
];

// The Integration Journey
const INTEGRATION_JOURNEY = [
  { stage: 'Belonging', provides: 'Safety', description: 'Ignition—belonging felt', color: '#059669' },
  { stage: 'Repair', provides: 'Resilience', description: 'Incubation—belonging stabilized', color: '#D97706' },
  { stage: 'Mission', provides: 'Meaning', description: 'Continuation—shared creation', color: '#8B5CF6' },
  { stage: 'Federation', provides: 'Durability', description: 'Planetary scale—success feeds others', color: '#2f6f3e' }
];

export const BelongingTools = ({ projectId }) => {
  const [activePhase, setActivePhase] = useState(0);
  const [responses, setResponses] = useState({
    blue: '',
    red: '',
    green: '',
    gold: ''
  });
  const [showQuickTools, setShowQuickTools] = useState(false);
  const [showFireRoles, setShowFireRoles] = useState(false);
  const [showBuilding, setShowBuilding] = useState(false);
  const [showFoundations, setShowFoundations] = useState(false);
  const [showCircle, setShowCircle] = useState(false);
  const [showJourney, setShowJourney] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [activeTab, setActiveTab] = useState('elchme');

  const currentPhase = ELCHME_PHASES[activePhase];

  const handleNext = () => {
    if (activePhase < ELCHME_PHASES.length - 1) {
      setActivePhase(activePhase + 1);
    }
  };

  const handlePrev = () => {
    if (activePhase > 0) {
      setActivePhase(activePhase - 1);
    }
  };

  const handleReset = () => {
    setActivePhase(0);
    setResponses({ blue: '', red: '', green: '', gold: '' });
  };

  return (
    <div className="p-4 h-full overflow-y-auto" data-testid="belonging-tools">
      {/* ElchmE Framework */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-heading text-sm font-semibold text-[var(--fg)]">
            ElchmE Framework
          </h4>
          <Button variant="ghost" size="sm" onClick={handleReset} className="text-xs">
            <RefreshCw size={12} className="mr-1" />
            Reset
          </Button>
        </div>

        {/* Phase Progress */}
        <div className="flex gap-1 mb-4">
          {ELCHME_PHASES.map((phase, idx) => (
            <button
              key={phase.key}
              onClick={() => setActivePhase(idx)}
              className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                idx <= activePhase ? 'opacity-100' : 'opacity-30'
              }`}
              style={{ backgroundColor: phase.color }}
              data-testid={`elchme-phase-${phase.key}`}
            />
          ))}
        </div>

        {/* Current Phase */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPhase.key}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="border-2" style={{ borderColor: currentPhase.color + '40' }}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: currentPhase.color + '20' }}
                  >
                    <currentPhase.icon size={24} style={{ color: currentPhase.color }} />
                  </div>
                  <div>
                    <CardTitle className="text-base" style={{ color: currentPhase.color }}>
                      {currentPhase.name}
                    </CardTitle>
                    <p className="text-xs text-[var(--fg)]/50">{currentPhase.description}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[var(--fg)]/70 mb-3 italic">
                  "{currentPhase.prompt}"
                </p>
                <Textarea
                  value={responses[currentPhase.key]}
                  onChange={(e) =>
                    setResponses({ ...responses, [currentPhase.key]: e.target.value })
                  }
                  placeholder="Write your response..."
                  className="min-h-[80px] text-sm"
                  data-testid={`elchme-input-${currentPhase.key}`}
                />
                <div className="flex justify-between mt-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePrev}
                    disabled={activePhase === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleNext}
                    disabled={activePhase === ELCHME_PHASES.length - 1}
                  >
                    Next
                    <ChevronRight size={14} className="ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* FIRE ROLES - The Practical Blueprint */}
      <div className="mb-6">
        <button
          onClick={() => setShowFireRoles(!showFireRoles)}
          className="flex items-center justify-between w-full text-left mb-3 group"
        >
          <div className="flex items-center gap-2">
            <Flame size={16} className="text-orange-500" />
            <h4 className="font-heading text-sm font-semibold text-[var(--fg)]">
              Fire Roles Blueprint
            </h4>
          </div>
          <ChevronDown
            size={16}
            className={`text-[var(--fg)]/50 transition-transform ${showFireRoles ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {showFireRoles && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-2 overflow-hidden"
            >
              <p className="text-xs text-[var(--fg)]/60 mb-3 italic">
                "How do you build a community where the fire actually lasts?"
              </p>
              
              {FIRE_ROLES.map((role) => (
                <motion.div key={role.key}>
                  <button
                    onClick={() => setSelectedRole(selectedRole === role.key ? null : role.key)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left"
                    style={{ 
                      backgroundColor: selectedRole === role.key ? role.color + '15' : 'var(--muted)',
                      opacity: selectedRole === role.key ? 1 : 0.7
                    }}
                    data-testid={`fire-role-${role.key}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: role.color + '20' }}
                    >
                      <role.icon size={20} style={{ color: role.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: role.color }}>{role.name}</p>
                      <p className="text-xs text-[var(--fg)]/50">{role.description}</p>
                    </div>
                    <ChevronRight 
                      size={14} 
                      className={`text-[var(--fg)]/30 transition-transform ${selectedRole === role.key ? 'rotate-90' : ''}`}
                    />
                  </button>
                  
                  <AnimatePresence>
                    {selectedRole === role.key && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div 
                          className="p-4 mt-1 rounded-lg border-l-4"
                          style={{ 
                            backgroundColor: role.color + '08',
                            borderColor: role.color 
                          }}
                        >
                          <ul className="space-y-1.5 mb-3">
                            {role.traits.map((trait, idx) => (
                              <li key={idx} className="text-xs text-[var(--fg)]/70 flex items-start gap-2">
                                <span className="text-[var(--fg)]/30 mt-0.5">•</span>
                                {trait}
                              </li>
                            ))}
                          </ul>
                          <p className="text-xs italic" style={{ color: role.color }}>
                            "{role.wisdom}"
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Community Building Essentials */}
      <div className="mb-6">
        <button
          onClick={() => setShowBuilding(!showBuilding)}
          className="flex items-center justify-between w-full text-left mb-3"
        >
          <div className="flex items-center gap-2">
            <Sprout size={16} className="text-[var(--primary)]" />
            <h4 className="font-heading text-sm font-semibold text-[var(--fg)]">
              Building Essentials
            </h4>
          </div>
          <ChevronDown
            size={16}
            className={`text-[var(--fg)]/50 transition-transform ${showBuilding ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {showBuilding && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-3 overflow-hidden"
            >
              {COMMUNITY_BUILDING.map((item) => (
                <div 
                  key={item.key}
                  className="p-3 rounded-xl bg-[var(--muted)]/30 border border-[var(--muted)]/50"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon size={14} className="text-[var(--primary)]" />
                    <p className="text-sm font-medium text-[var(--fg)]">{item.title}</p>
                  </div>
                  <p className="text-xs text-[var(--fg)]/60 leading-relaxed">
                    {item.content}
                  </p>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Quick Tools */}
      <div className="mb-6">
        <button
          onClick={() => setShowQuickTools(!showQuickTools)}
          className="flex items-center justify-between w-full text-left mb-3"
        >
          <h4 className="font-heading text-sm font-semibold text-[var(--fg)]">
            Quick Tools
          </h4>
          <ChevronRight
            size={16}
            className={`text-[var(--fg)]/50 transition-transform ${showQuickTools ? 'rotate-90' : ''}`}
          />
        </button>

        <AnimatePresence>
          {showQuickTools && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-2 overflow-hidden"
            >
              {QUICK_TOOLS.map((tool) => (
                <button
                  key={tool.key}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-[var(--muted)]/30 hover:bg-[var(--muted)]/50 transition-colors text-left"
                  data-testid={`quick-tool-${tool.key}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-[var(--primary)]/10 flex items-center justify-center">
                    <tool.icon size={16} className="text-[var(--primary)]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--fg)]">{tool.name}</p>
                    <p className="text-xs text-[var(--fg)]/50">{tool.description}</p>
                  </div>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Foundational Truths */}
      <div className="mb-6">
        <button
          onClick={() => setShowFoundations(!showFoundations)}
          className="flex items-center justify-between w-full text-left mb-3"
        >
          <div className="flex items-center gap-2">
            <Target size={16} className="text-[var(--primary)]" />
            <h4 className="font-heading text-sm font-semibold text-[var(--fg)]">
              Foundational Truths
            </h4>
          </div>
          <ChevronDown
            size={16}
            className={`text-[var(--fg)]/50 transition-transform ${showFoundations ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {showFoundations && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-2 overflow-hidden"
            >
              <p className="text-xs text-[var(--fg)]/60 mb-3 italic">
                "The invisible load-bearing beams of community"
              </p>
              {FOUNDATIONAL_TRUTHS.map((truth) => (
                <div 
                  key={truth.key}
                  className="p-3 rounded-xl bg-gradient-to-r from-[var(--primary)]/10 to-[var(--primary)]/5 border border-[var(--primary)]/20"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <truth.icon size={14} className="text-[var(--primary)]" />
                    <p className="text-sm font-medium text-[var(--primary)]">{truth.title}</p>
                  </div>
                  <p className="text-xs text-[var(--fg)]/70 leading-relaxed">
                    {truth.content}
                  </p>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Circle Process */}
      <div className="mb-6">
        <button
          onClick={() => setShowCircle(!showCircle)}
          className="flex items-center justify-between w-full text-left mb-3"
        >
          <div className="flex items-center gap-2">
            <Circle size={16} className="text-[var(--accent)]" />
            <h4 className="font-heading text-sm font-semibold text-[var(--fg)]">
              Circle Process
            </h4>
          </div>
          <ChevronDown
            size={16}
            className={`text-[var(--fg)]/50 transition-transform ${showCircle ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {showCircle && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="space-y-2 overflow-hidden"
            >
              <p className="text-xs text-[var(--fg)]/60 mb-3 italic">
                "How gatherings hold people"
              </p>
              {CIRCLE_PROCESS.map((item) => (
                <div 
                  key={item.key}
                  className="p-3 rounded-xl border-l-4"
                  style={{ backgroundColor: item.color + '08', borderColor: item.color }}
                >
                  <p className="text-sm font-medium mb-1" style={{ color: item.color }}>{item.title}</p>
                  <p className="text-xs text-[var(--fg)]/60 leading-relaxed">
                    {item.content}
                  </p>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Integration Journey */}
      <div className="mb-6">
        <button
          onClick={() => setShowJourney(!showJourney)}
          className="flex items-center justify-between w-full text-left mb-3"
        >
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-amber-500" />
            <h4 className="font-heading text-sm font-semibold text-[var(--fg)]">
              The Integration Journey
            </h4>
          </div>
          <ChevronDown
            size={16}
            className={`text-[var(--fg)]/50 transition-transform ${showJourney ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {showJourney && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <p className="text-xs text-[var(--fg)]/60 mb-3 italic">
                "From connection to coherence, from coherence to collective thriving"
              </p>
              <div className="space-y-2">
                {INTEGRATION_JOURNEY.map((stage, idx) => (
                  <div key={stage.stage} className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: stage.color }}
                    >
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--fg)]">{stage.stage}</span>
                        <ArrowRight size={12} className="text-[var(--fg)]/30" />
                        <span className="text-xs text-[var(--fg)]/50">{stage.provides}</span>
                      </div>
                      <p className="text-xs text-[var(--fg)]/40">{stage.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[var(--primary)] mt-3 italic text-center">
                "Toward a world we want to live in, for 10,000 generations"
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Belonging Reminder */}
      <div className="p-4 rounded-xl bg-[var(--primary)]/5 border border-[var(--primary)]/20">
        <div className="flex items-start gap-3">
          <Users size={18} className="text-[var(--primary)] mt-0.5" />
          <div>
            <p className="text-xs font-medium text-[var(--primary)] mb-1">Remember</p>
            <p className="text-xs text-[var(--fg)]/70 leading-relaxed">
              This is how a community becomes a village—where Stewards are supported, Sitters are guided, Runners are stabilized, and the fire is held by the system, not the founder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BelongingTools;
